import { useState, useEffect } from "react";
import ProfileSidebar from "../../components/ProfileSidebar/ProfileSidebar";
import { useBookingHistory } from "../../hooks";
import { useNavigate } from "react-router-dom";
import RefundDetailsSection from "../../components/RefundDetailsSection";
import RefundStatusBadge from "../../components/RefundStatusBadge";
import { useCurrencyDisplay } from "../../../hooks/useCurrencyDisplay";
import {
  getBookingLegFarePerPax,
  getBookingLegItineraryTotal,
} from "../../../utils/bookingDisplayFare";
import {
  deriveGroupBookingStatus,
  isCancelledLegStatus,
  isManageableLegStatus,
} from "../../../utils/bookingLegStatus";

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return "/assets/images/no_aircraft.png";

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }

  // If it's a relative path, prepend the API base URL (without /api/v1)
  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL || "https://jetscanner360.com/api/v1";
  const baseUrl = apiBaseUrl.replace("/api/v1", "");
  return `${baseUrl}/${imageUrl}`;
};

// Status Badge Component
const StatusBadge = ({ flight, refundStatus }) => {
  if (refundStatus) {
    return <RefundStatusBadge refundStatus={refundStatus} />;
  }
  if (!flight) return null;

  if (flight.booking_status === 'RejectedByPJO') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 animate-pulse">
        <i className="fa fa-exclamation-triangle"></i>
        Action Required — Flight Declined
      </span>
    );
  }

  if (flight.booking_status === 'PartiallyActive' && flight.active_leg_count != null) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
        <i className="fa fa-plane"></i>
        {flight.active_leg_count} upcoming
        {flight.cancelled_leg_count > 0 ? ` · ${flight.cancelled_leg_count} cancelled` : ''}
      </span>
    );
  }

  // Cancelled takes priority — check it first regardless of other fields
  if (isCancelledLegStatus(flight.booking_status)) {
    if (flight.cancelled_by === 'airliner' || flight.rejected_at) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
          <i className="fa fa-times-circle"></i>
          Rejected by Airliner
        </span>
      );
    }
    if (flight.cancelled_by === 'system') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
          <i className="fa fa-exclamation-circle"></i>
          Auto-Cancelled
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
        <i className="fa fa-ban"></i>
        Cancelled
      </span>
    );
  }

  // Only show "Waiting for Airliner" when booking_status is exactly PendingPJOConfirmation
  if (flight.booking_status === 'PendingPJOConfirmation') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
        <i className="fa fa-hourglass-half"></i>
        Waiting for Airliner
      </span>
    );
  }

  return null;
};

const BookingHistoryPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Upcoming");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const {
    bookings,
    pagination,
    loading,
    loadBookingHistory,
  } = useBookingHistory();

  // Currency display hook
  const { getFormattedWithBoth } = useCurrencyDisplay();

  const [filters, setFilters] = useState({
    searchRef: "",
    depSearch: "",
    arrSearch: "",
    fromDate: "",
    toDate: "",
    sortOrder: "DESC",
  });

  const tabs = ["Upcoming", "Completed", "Cancelled"];

  useEffect(() => {
    loadBookingHistory({
      status: activeTab,
      page: currentPage,
      limit: pagination.limit,
      ...filters
    });
  }, [loadBookingHistory, activeTab, currentPage, filters, pagination.limit]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when tab changes
    setExpandedBooking(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedBooking(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to first page when applying new filters
    loadBookingHistory({ status: activeTab, page: 1, limit: pagination.limit, ...filters });
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      searchRef: "",
      depSearch: "",
      arrSearch: "",
      fromDate: "",
      toDate: "",
      sortOrder: "DESC",
    };
    setFilters(defaultFilters);
    setCurrentPage(1);
    loadBookingHistory({ status: activeTab, page: 1, limit: pagination.limit, ...defaultFilters });
  };

  const getCurrentBookings = () => {
    switch (activeTab) {
      case "Upcoming":
        return bookings.upcoming;
      case "Completed":
        return bookings.completed;
      case "Cancelled":
        return bookings.cancelled;
      default:
        return [];
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const calculateDuration = (departure, arrival) => {
    const diff = new Date(arrival) - new Date(departure);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours} Hrs ${mins} Mins`;
  };

  const toggleBookingDetails = (referenceNumber) => {
    setExpandedBooking(
      expandedBooking === referenceNumber ? null : referenceNumber,
    );
  };

  const currentBookings = getCurrentBookings();

  // Sort bookings by date based on sortOrder filter
  const sortedBookings = [...currentBookings].sort((a, b) => {
    // Get the most recent date from each booking
    const getBookingDate = (booking) => {
      if (booking.is_multi_leg && booking.flights && booking.flights.length > 0) {
        // For multi-leg, use the latest departure date
        return new Date(
          Math.max(...booking.flights.map(f => new Date(f.departure_date_time)))
        );
      }
      return new Date(booking.departure_date_time || booking.created_at);
    };

    const dateA = getBookingDate(a);
    const dateB = getBookingDate(b);

    // Sort based on sortOrder filter
    return filters.sortOrder === 'DESC' ? dateB - dateA : dateA - dateB;
  });

  // Group bookings by reference number (in case there are multiple flights per booking)
  const groupedBookings = sortedBookings.reduce((acc, booking) => {
    const ref = booking.reference_number;

    if (!acc[ref]) {
      acc[ref] = {
        flights: [],
        refund_status: booking.refund_status, // Store refund_status at the group level
        booking_status: booking.booking_status,
        cancelled_by: booking.cancelled_by
      };
    }

    if (!acc[ref].refund_status && booking.refund_status) {
      acc[ref].refund_status = booking.refund_status;
    }
    if (booking.booking_status === "PendingPJOConfirmation") {
      acc[ref].booking_status = "PendingPJOConfirmation";
    } else if (booking.booking_status === "RejectedByPJO") {
      acc[ref].booking_status = "RejectedByPJO";
    } else if (
      (booking.booking_status === "Cancelled" || booking.booking_status === "Cancelled (Refunded)") &&
      acc[ref].booking_status !== "PendingPJOConfirmation" &&
      acc[ref].booking_status !== "RejectedByPJO"
    ) {
      acc[ref].booking_status = booking.booking_status;
    }
    if (!acc[ref].cancelled_by && booking.cancelled_by) {
      acc[ref].cancelled_by = booking.cancelled_by;
    }

    if (booking.is_multi_leg && booking.flights && booking.flights.length > 0) {
      acc[ref].flights.push(
        ...booking.flights.map((f) => ({
          ...f,
          bookingToken: f.booking_token || booking.booking_token,
        })),
      );
    } else {
      acc[ref].flights.push({
        ...booking,
        bookingToken: booking.booking_token,
      });
    }

    return acc;
  }, {});

  Object.keys(groupedBookings).forEach((ref) => {
    const group = groupedBookings[ref];
    const legs = group.flights || [];
    const activeCount = legs.filter((leg) => isManageableLegStatus(leg.booking_status)).length;
    const cancelledCount = legs.filter((leg) => isCancelledLegStatus(leg.booking_status)).length;

    group.booking_status = deriveGroupBookingStatus(legs, group.booking_status);
    group.active_leg_count = activeCount;
    group.cancelled_leg_count = cancelledCount;
  });

  return (
    <div className="bg-jet-gray-100 pt-[40px] pb-8 px-3 sm:px-5">
      <div className="bg-white p-[28px] rounded-[10px] max-w-7xl mx-auto flex flex-col lg:flex-row gap-5">
        {/* Left Card - My Account */}
        <ProfileSidebar />

        {/* Right Card - Booking History */}
        <div className="flex-1 bg-white rounded-lg shadow-jet overflow-hidden">
          {/* Tab Buttons */}
          <div className="flex gap-3 p-4 bg-white">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`flex-1 py-3 px-4 rounded-[4px] text-sm font-semibold cursor-pointer transition-all duration-300 ${activeTab === tab
                  ? "bg-jet-yellow text-white"
                  : "bg-gray-200 text-jet-gray-600 hover:bg-gray-300"
                  }`}
                onClick={() => handleTabChange(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-4 sm:p-8">
            <div className="border-2 border-gray-200 rounded-lg mb-6 transition-all duration-200">
              {/* Header */}
              <div
                onClick={() => setShowFilters(!showFilters)}
                className="group flex justify-between items-center p-3 cursor-pointer bg-white hover:bg-orange-400 hover:border-yellow-400 transition-all duration-200 border-2 border-orange-400 rounded-lg"
              >
                <div className="flex items-center gap-3 font-medium text-gray-700 group-hover:text-white">
                  <i className="fa fa-filter text-gray-500 group-hover:text-white text-lg"></i>
                  <span className="text-base">Filter & Sort Bookings</span>
                </div>

                <div className="text-gray-500 group-hover:text-white text-lg">
                  {showFilters ? "▲" : "▼"}
                </div>
              </div>

              {/* Expandable Content */}
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showFilters ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                {showFilters && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                      {/* Booking Reference */}
                      <div className="space-y-1">
                        <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                          <i className="fa fa-hashtag text-gray-400 mr-1 w-3 text-xs"></i>
                          Booking Reference
                        </label>
                        <input
                          type="text"
                          name="searchRef"
                          value={filters.searchRef}
                          onChange={handleFilterChange}
                          placeholder="Enter reference number"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                        />
                      </div>

                      {/* From (Departure) */}
                      <div className="space-y-1">
                        <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                          <i className="fa fa-map-marker-alt text-gray-400 mr-1 w-3 text-xs"></i>
                          From (Departure)
                        </label>
                        <input
                          type="text"
                          name="depSearch"
                          value={filters.depSearch}
                          onChange={handleFilterChange}
                          placeholder="City, airport name or code"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                        />
                      </div>

                      {/* To (Arrival) */}
                      <div className="space-y-1">
                        <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                          <i className="fa fa-map-marker-alt text-gray-400 mr-1 w-3 text-xs"></i>
                          To (Arrival)
                        </label>
                        <input
                          type="text"
                          name="arrSearch"
                          value={filters.arrSearch}
                          onChange={handleFilterChange}
                          placeholder="City, airport name or code"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                        />
                      </div>

                      {/* Date From */}
                      <div className="space-y-1">
                        <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                          <i className="fa fa-calendar text-gray-400 mr-1 w-3 text-xs"></i>
                          Date From
                        </label>
                        <input
                          type="date"
                          name="fromDate"
                          value={filters.fromDate}
                          onChange={handleFilterChange}
                          placeholder="dd/mm/yyyy"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                        />
                      </div>

                      {/* Date To */}
                      <div className="space-y-1">
                        <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                          <i className="fa fa-calendar text-gray-400 mr-1 w-3 text-xs"></i>
                          Date To
                        </label>
                        <input
                          type="date"
                          name="toDate"
                          value={filters.toDate}
                          onChange={handleFilterChange}
                          placeholder="dd/mm/yyyy"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                        />
                      </div>

                      {/* Sort By */}
                      <div className="space-y-1">
                        <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                          <i className="fa fa-sort text-gray-400 mr-1 w-3 text-xs"></i>
                          Sort By
                        </label>
                        <select
                          name="sortOrder"
                          value={filters.sortOrder}
                          onChange={handleFilterChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                        >
                          <option value="DESC">Date: Newest First</option>
                          <option value="ASC">Date: Oldest First</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end mt-4 gap-3">
                      <button
                        onClick={handleClearFilters}
                        className="bg-gray-200 hover:bg-gray-300 px-6 py-2 rounded-md font-semibold text-sm text-gray-700 transition-all duration-200 flex items-center gap-2 shadow-sm"
                      >
                        <i className="fa fa-times text-xs"></i>
                        Clear Filters
                      </button>
                      <button
                        onClick={handleApplyFilters}
                        className="bg-yellow-400 hover:bg-yellow-500 px-6 py-2 rounded-md font-semibold text-sm text-gray-900 transition-all duration-200 flex items-center gap-2 shadow-sm"
                      >
                        <i className="fa fa-search text-xs"></i>
                        Apply Filters
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
                  <p className="text-jet-gray-600">
                    Loading booking history...
                  </p>
                </div>
              </div>
            ) : Object.keys(groupedBookings).length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-lg text-jet-gray-500 font-medium">
                    No {activeTab.toLowerCase()} bookings found
                  </div>
                  <p className="text-sm text-jet-gray-400 mt-2">
                    {activeTab === "Upcoming" &&
                      "Book your next flight to see it here."}
                    {activeTab === "Completed" &&
                      "Your completed flights will appear here."}
                    {activeTab === "Cancelled" &&
                      "Your cancelled bookings will appear here."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupedBookings).map(
                  ([referenceNumber, bookingGroup]) => (
                    <div
                      key={referenceNumber}
                      className={`card mb-3 border rounded-lg overflow-hidden transition-all duration-300 ${
                        bookingGroup.booking_status === 'RejectedByPJO'
                          ? 'border-amber-400 ring-1 ring-amber-200'
                          : 'border-jet-gray-200'
                      }`}
                    >
                      {/* Collapsible Header - Booking Reference Number */}
                      <button
                        onClick={() => toggleBookingDetails(referenceNumber)}
                        className={`w-full text-left px-4 py-3 hover:bg-jet-gray-50 transition-colors font-semibold text-jet-gray-900 flex justify-between items-center ${expandedBooking === referenceNumber
                          ? "bg-jet-yellow/5 border-2 border-jet-yellow rounded-t-lg"
                          : "bg-white"
                          }`}
                        style={{
                          fontSize: "16px",
                        }}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span>Booking Reference Number: {referenceNumber}</span>
                          {bookingGroup.flights && bookingGroup.flights.length > 0 && (
                            <StatusBadge
                              flight={{
                                ...(bookingGroup.flights[0] || {}),
                                booking_status: bookingGroup.booking_status || bookingGroup.flights[0]?.booking_status,
                                cancelled_by: bookingGroup.cancelled_by || bookingGroup.flights[0]?.cancelled_by,
                                active_leg_count: bookingGroup.active_leg_count,
                                cancelled_leg_count: bookingGroup.cancelled_leg_count,
                              }}
                              refundStatus={bookingGroup.refund_status}
                            />
                          )}
                        </div>
                        <span className={`text-xs transition-transform duration-300 ${expandedBooking === referenceNumber ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>


                      {/* Collapsible Content - Flight Details */}
                      <div
                        className="transition-all duration-700 ease-in-out overflow-hidden"
                        style={{
                          maxHeight: expandedBooking === referenceNumber ? "1000px" : "0",
                          opacity: expandedBooking === referenceNumber ? 1 : 0,
                        }}
                      >
                        <div className="p-4 bg-white"
                          onClick={() => {
                            navigate(
                              `/jetsetter/booking-details/${referenceNumber}`,
                            );
                          }}>
                          {bookingGroup.booking_status === 'RejectedByPJO' && (
                            <div
                              className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div>
                                <p className="text-sm font-bold text-amber-950">
                                  Your flight was declined — action required
                                </p>
                                <p className="text-xs text-amber-900/80 mt-0.5">
                                  Choose a refund or change flight before the deadline.
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/jetsetter/booking-details/${referenceNumber}`)
                                }
                                className="shrink-0 bg-jet-yellow hover:bg-yellow-500 text-gray-900 font-bold text-sm px-4 py-2 rounded shadow-sm"
                              >
                                Choose an option
                              </button>
                            </div>
                          )}

                          {/* Refund Details Section — not shown while PJO choice is pending */}
                          {bookingGroup.flights &&
                            bookingGroup.flights.length > 0 &&
                            bookingGroup.refund_status &&
                            bookingGroup.booking_status !== 'RejectedByPJO' && (
                            <RefundDetailsSection refundStatus={bookingGroup.refund_status} />
                          )}

                          {bookingGroup.flights.map((flight, index) => (
                            <div key={flight.id}>
                              {/* Flight Row */}
                              <div className="grid grid-cols-12 gap-2 items-center py-2 max-sm:grid-cols-1">
                                {/* Column 1: Aircraft Image */}
                                <div className="col-span-2 max-sm:col-span-1 max-sm:w-full max-sm:max-w-[200px]">
                                  {flight.aircraft_images &&
                                    flight.aircraft_images.length > 0 ? (
                                    <img
                                      src={getImageUrl(
                                        flight.aircraft_images[0]
                                          .presigned_url ||
                                        flight.aircraft_images[0].url,
                                      )}
                                      alt={
                                        flight.aircraft_images[0].name ||
                                        "Aircraft"
                                      }
                                      className="w-full h-auto object-cover rounded"
                                    />
                                  ) : (
                                    <img
                                      src="/assets/images/no_aircraft.png"
                                      alt="Aircraft"
                                      className="w-full h-auto object-cover rounded"
                                    />
                                  )}
                                </div>

                                {/* Column 2: Flight Number & Departure */}
                                <div className="col-span-4 max-sm:col-span-1 rounded-md">
                                  <div
                                    onClick={() => {
                                      // Navigate using reference_number to show entire booking (all legs)
                                      navigate(
                                        `/jetsetter/booking-details/${referenceNumber}`,
                                      );
                                      console.log(
                                        "navigating to booking reference",
                                        referenceNumber,
                                      );
                                    }}
                                    className="text-sm font-bold text-gray-900 cursor-pointer hover:text-jet-yellow hover:underline transition-all duration-200 inline-flex items-center gap-2 flex-wrap px-2 py-0.5 mb-1"
                                  >
                                    <span>{flight.flight_number}</span>
                                    {flight.booking_status && (
                                      <span
                                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                          isManageableLegStatus(flight.booking_status)
                                            ? 'bg-blue-100 text-blue-700'
                                            : isCancelledLegStatus(flight.booking_status)
                                              ? 'bg-gray-100 text-gray-600'
                                              : 'bg-amber-100 text-amber-700'
                                        }`}
                                      >
                                        {flight.booking_status}
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    className="text-sm font-bold text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap"
                                    title={`${flight.departure_airport_name || flight.departure_airport} ${flight.departure_airport_iata ? `(${flight.departure_airport_iata})` : ''} ${formatTime(flight.departure_date_time)}`}
                                  >
                                    {flight.departure_airport_name ||
                                      flight.departure_airport}{" "}
                                    {flight.departure_airport_iata ? `(${flight.departure_airport_iata})` : ''}{" "}
                                    {formatTime(flight.departure_date_time)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {formatDate(flight.departure_date_time)}
                                  </div>
                                </div>

                                {/* Column 3: Arrow & Duration */}
                                <div className="col-span-2 max-sm:col-span-1 text-center max-sm:flex max-sm:items-center max-sm:gap-2 max-sm:py-1">
                                  <div className="text-xl text-gray-400">→</div>
                                  <div className="text-[10px] text-gray-500 whitespace-nowrap">
                                    {calculateDuration(
                                      flight.departure_date_time,
                                      flight.arrival_date_time,
                                    )}
                                  </div>
                                </div>

                                {/* Column 4: Arrival */}
                                <div className="col-span-4 max-sm:col-span-1">
                                  <div className="h-[26px] max-sm:hidden"></div> {/* Spacer for alignment with Flight No line */}
                                  <div
                                    className="text-sm font-bold text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap"
                                    title={`${flight.arrival_airport_name || flight.arrival_airport} ${flight.arrival_airport_iata ? `(${flight.arrival_airport_iata})` : ''} ${formatTime(flight.arrival_date_time)}`}
                                  >
                                    {flight.arrival_airport_name ||
                                      flight.arrival_airport}{" "}
                                    {flight.arrival_airport_iata ? `(${flight.arrival_airport_iata})` : ''}{" "}
                                    {formatTime(flight.arrival_date_time)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {formatDate(flight.arrival_date_time)}
                                  </div>
                                </div>
                              </div>

                              {/* Fare Details on "Last Line" */}
                              <div className="flex gap-6 mt-1 ml-[16.666%] pl-2 max-sm:ml-0 max-sm:pl-0 max-sm:flex-wrap max-sm:gap-4">
                                <div className="text-sm text-gray-800">
                                  Fare/Pax{" "}
                                  <span className="font-bold">
                                    {getFormattedWithBoth(
                                      getBookingLegFarePerPax(flight)
                                    )}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-800">
                                  Total Fare{" "}
                                  <span className="font-bold">
                                    {getFormattedWithBoth(
                                      getBookingLegItineraryTotal(flight)
                                    )}
                                  </span>
                                </div>
                              </div>

                              {/* Separator between flights */}
                              {index < bookingGroup.flights.length - 1 && (
                                <div className="border-t border-gray-200 my-4"></div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                )}

                {pagination && pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages + 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 4) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 3) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="flex justify-center items-center gap-1 mt-8 mb-4">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <span className="text-[10px]">◀</span>
      </button>

      {getPageNumbers().map((page, index) => (
        <button
          key={index}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          disabled={page === '...'}
          className={`w-8 h-8 flex items-center justify-center border text-sm transition-all duration-200 rounded ${page === currentPage
              ? 'bg-jet-yellow border-jet-yellow text-white font-bold'
              : page === '...'
                ? 'border-transparent cursor-default'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <span className="text-[10px]">▶</span>
      </button>
    </div>
  );
};

export default BookingHistoryPage;

